import type {
  StoreReorderLineSelection,
  StoreReorderResponse
} from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateSellerCarts } from "../cart/cache";

export interface ReorderConfirmInput {
  orderId: string;
  /** Buyer-picked subset + quantities; omit to add every addable line. */
  selections?: StoreReorderLineSelection[];
}

/**
 * Previews a reorder: resolves a past order against live offers and returns the
 * changed-line diff WITHOUT mutating any cart. Drives the review dialog.
 *
 * @example
 * ```tsx
 * const { mutateAsync: preview, isPending } = useReorderPreview();
 * const result = await preview(order.id);
 * ```
 */
export function useReorderPreview() {
  return useMutation<StoreReorderResponse, Error, string>({
    mutationFn: (orderId: string) => sdk.store.reorder.preview(orderId)
  });
}

/**
 * Confirms a reorder: adds the available + repriced lines of a past order to the
 * customer's seller cart, or - when `selections` is given - just the buyer's
 * picked subset at their picked quantities. Invalidates the seller carts so the
 * cart UI refreshes.
 *
 * @example
 * ```tsx
 * const { mutateAsync: confirm } = useReorderConfirm();
 * const { cart_id } = await confirm({ orderId: order.id, selections });
 * ```
 */
export function useReorderConfirm() {
  const queryClient = useQueryClient();

  return useMutation<StoreReorderResponse, Error, ReorderConfirmInput>({
    mutationFn: ({ orderId, selections }: ReorderConfirmInput) =>
      sdk.store.reorder.confirm(orderId, selections),

    onSettled: () => {
      invalidateSellerCarts(queryClient);
    }
  });
}
